import { useRef } from "react";
import { AbortableRequestsContext, AbortableRequestsContextType } from "./AbortableRequestsContext";
import { ObservableSubscription } from "@apollo/client";

export const AbortableRequestsProvider: React.FCWithChildren = ({ children }) => {
    const observables = useRef<{ [key: string]: ObservableSubscription | null }>({});
    const suspended = useRef<{ [key: string]: number }>({});
    const timeouts = useRef<{ [key: string]: ReturnType<typeof setTimeout> }>({});

    const fireRequestImmediately = (channel: string, id: string, fireRequest: () => ObservableSubscription) => {
        const key = `${channel}-${id}`;
        observables.current[key] = fireRequest();
    };

    const fireRequestWithDelay = (channel: string, id: string, fireRequest: () => ObservableSubscription) => {
        const key = `${channel}-${id}`;
        clearTimeout(timeouts.current[key]);
        timeouts.current[key] = setTimeout(() => {
            if (suspended.current[key] === 0) fireRequestImmediately(channel, id, fireRequest);
            else fireRequestWithDelay(channel, id, fireRequest);
        }, 100);
    };

    const setAbortableRequest: AbortableRequestsContextType["setAbortableRequest"] = (channel, id, fireRequest) => {
        const key = `${channel}-${id}`;
        if (suspended.current[key]) fireRequestWithDelay(channel, id, fireRequest);
        else fireRequestImmediately(channel, id, fireRequest);
    };

    const abortPendingRequest: AbortableRequestsContextType["abortPendingRequest"] = (channel, id) => {
        observables.current[`${channel}-${id}`]?.unsubscribe();
    };

    const pauseIncomingRequests = (channel: string, id: string) => {
        const key = `${channel}-${id}`;
        suspended.current[key] = (suspended.current[key] || 0) + 1;
    };

    const resumeIncomingRequests = (channel: string, id: string) => {
        const key = `${channel}-${id}`;
        suspended.current[key] = (suspended.current[key] || 0) - 1;
    };

    return (
        <AbortableRequestsContext.Provider value={{ setAbortableRequest, abortPendingRequest, pauseIncomingRequests, resumeIncomingRequests }}>
            {children}
        </AbortableRequestsContext.Provider>
    );
};
